import { LitElement, html, css } from 'lit';
import { unsafeHTML } from 'lit/directives/unsafe-html.js';
import { accessibility, containers, grid, sidebar } from '../../utilities/styles';
import { cardLinks } from '../../utilities/accessibility';
import { EventManager } from '../../utilities/events';
import { arrowBack, arrow } from '../../utilities/icons';
import { handle403s } from '../../utilities/AllInErrorHandling';
export class AllInListingsSupportProfileManager extends LitElement {
    static properties = {
        restUrl: { type: String },
        nonce: { type: String },
        pageSize: { type: Number },
        params: { type: Object },
        name: { type: String },
        profiles: { type: Array },
        total: { type: Number },
        supportTypes: { type: Array },
        operationIn: { type: Array },
        artforms: { type: Array },
        languages: { type: Array },
        _baseurl: { type: String },
        hardlinks: { type: Object },
        _language: { type: String },
        _search: { type: String },
        _addressSearch: { type: String },
        _personalisation: { type: Object },
        _supportTypes: { type: Array },
        _pageNumber: { type: Number },
        _artforms: { state: true, type: Array },
        _operationIn: { state: true, type: Array },
        _currentProfile: { state: true, type: String },
        _currentProfileSidebar: { state: true, type: String },
        _currentProfileData: { state: true, type: Object },
        _profiles: { state: true, type: Array },
        _total: { state: true, type: Number },
    };
    constructor() {
        super();
        if (!this._profiles) {
            this._profiles = [];
        }
    }
    async connectedCallback() {
        super.connectedCallback();
        this.eventManager = new EventManager(this);
        this.eventManager.listen(`allin-navigation-${this.name}`, this._updateSidebar.bind(this));
        this.eventManager.listen(`allin-support-go-back-${this.name}`, this._handleGoBack.bind(this));
        this.eventManager.listen(`allin-support-profiles-filters-${this.name}`, this._setFilters.bind(this));
        this.eventManager.listen(`allin-change-page-${this.name}`, this._handleChangePage.bind(this));
        this.eventManager.listen(`allin-profile-setter-${this.name}`, this._updateCurrentProfile.bind(this));
        this.eventManager.listen('popstate', this._paramsFromPopState);
        if (this.params) {
            this._search = this.params.search ?? '';
            this._addressSearch = this.params.addressSearch ?? '';
            //this._personalisation = this.params.personalisation??{};
            this._artforms = this.params.artforms ? this.params.artforms.split(',') : [];
            this._supportTypes = this.params.supportTypes ? this.params.supportTypes.split(',') : [];
            this._operationIn = this.params.operationIn ? this.params.operationIn.split(',') : [];
            this._pageNumber = this.params.pageNumber ?? 1;
            this._currentProfile = this.params.currentSupportProfile ?? '';
            this._profiles[this._pageNumber] = this.profiles ?? [];
            this._language = this.params.language ?? '';
        }
        if (this.total) {
            this._total = this.total;
        }
        this._baseurl = this.hardlinks['listings_support'];
        this.eventManager.initHistory({ params: JSON.stringify(this.params) });
        cardLinks(this.shadowRoot);
    }
    _paramsFromPopState(e) {
        this._handleGoBack();
    }
    disconnectedCallback() {
        super.disconnectedCallback();
        this.eventManager.disconnect();
    }
    _setDocumentTitle(title) {
        document.title = `${title} - All In`;
    }
    async firstUpdated() {
        if (!this._currentProfile && !this._profiles.length) {
            await this._fetchProfiles();
        }
        cardLinks(this.shadowRoot);
    }
    get grid() {
        return this.shadowRoot.querySelector('.grid.cards');
    }
    get legend() {
        return document.getElementById('legend');
    }
    async updated(changedProperties) {
        window.scrollTo(0, 0);
        if (changedProperties.has('_currentProfile')) {
            if (this._currentProfile) {
                this.eventManager.addHistory(this._baseurl + this._currentProfile, { params: JSON.stringify({ ...this.params, currentProfile: this._currentProfile }) });
                if (!this._currentProfileData) {
                    for (let page in this._profiles) {
                        this._currentProfileData = this._profiles[page].find(profile => profile.id === this._currentProfile);
                        if (this._currentProfileData)
                            break;
                    }
                }
                let profName = this._currentProfileData?.name ?? '';
                this._setDocumentTitle(profName);
                this.eventManager.emit(`allin-header-${this.name}`, { name: this.name, text: profName });
                this.eventManager.emit(`allin-header-location-listings-subtitle`, { name: 'location-listings-subtitle', text: '' });
                if (profName) {
                    let crumb = {
                        listing: { url: `${this._baseurl}`, title: `Support` },
                        panel: { url: `${this._baseurl}${this._currentProfile}`, title: profName }
                    };
                    this.eventManager.emit(`allin-breadcrumb-${this.name}`, crumb);
                }
                this.renderRoot.querySelector('button,a')?.focus();
            }
            else {
                const params = new URLSearchParams();
                if (this._search)
                    params.append('search', this._search);
                if (this._addressSearch)
                    params.append('addressSearch', this._addressSearch);
                if (this._personalisation)
                    params.append('personalisation', JSON.stringify(this._personalisation));
                if (this._supportTypes && this._supportTypes.length)
                    params.append('supportTypes', this._supportTypes.join(','));
                if (this._operationIn && this._operationIn.length)
                    params.append('operationIn', this._operationIn.join(','));
                if (this._artforms && this._artforms.length)
                    params.append('artforms', this._artforms.join(','));
                if (this._pageNumber)
                    params.append('pageNumber', this._pageNumber);
                if (this._language)
                    params.append('language', this._language);
                if (params) {
                    this.eventManager.addHistory(`${this._baseurl}?${params.toString()}`, { params: JSON.stringify({ ...this.params }) });
                }
                this._setDocumentTitle('Support');
                this.eventManager.emit(`allin-header-${this.name}`, { name: this.name, revert: true });
                this.eventManager.emit(`allin-header-location-listings-subtitle`, { name: 'location-listings-subtitle', text: '' });
                let crumb = {
                    listing: { url: `${this._baseurl}`, title: `Support` },
                };
                this.eventManager.emit(`allin-breadcrumb-${this.name}`, crumb);
                this.legend?.classList.remove('hide');
            }
        }
        if (changedProperties.has('params')) {
            this._search = this.params.search ?? '';
            this._addressSearch = this.params.addressSearch ?? '';
            //this._personalisation = this.params.personalisation??{};
            if (typeof this.params.supportTypes === 'string') {
                this.params._supportTypes = this.params.supportTypes?.split(',');
            }
            this._supportTypes = this.params.supportTypes ? this.params.supportTypes : [];
            if (typeof this.params.artforms === 'string') {
                this.params._artforms = this.params.artforms.split(',');
            }
            this._artforms = this.params.artforms ? this.params.artforms : [];
            if (typeof this.params.operationIn === 'string') {
                this.params._operationIn = this.params.operationIn?.split(',');
            }
            this._operationIn = this.params.operationIn ? this.params.operationIn : [];
            this._pageNumber = this.params.pageNumber ?? 1;
            this._currentSupportProfile = this.params.currentSupportProfile ?? '';
            this._profiles[this._pageNumber] = this.profiles ?? [];
            this._language = this.params.language ?? '';
        }
        cardLinks(this.shadowRoot);
    }
    render() {
        let main;
        let aside;
        if (this._currentProfile) {
            let profileData;
            if (this._currentProfileData) {
                profileData = this._currentProfileData;
            }
            else {
                profileData = this._profiles[this._pageNumber]?.find(profile => profile.id === this._currentProfile);
            }
            main = html `\n            <allin-support\n                profile="${this._currentProfile}"\n                profiledata='${JSON.stringify(profileData)}'\n                resturl="${this.restUrl}"\n                nonce="${this.nonce}"\n                name="${this.name}"\n                hardlinks='${JSON.stringify(this.hardlinks)}'\n            >\n            </allin-support>\n           `;
            aside = html `<div>${this._currentProfileSidebar}</div>`;
            return html `<div class="grid two_column">\n            <div class="return back-to-search">\n                <a href="#" class="back-to-search-btn" @click=${this._handleGoBack}><span aria-hidden=true>${arrowBack}</span>Go back to search</a>\n            </div>\n            <aside class="sidebar">${aside}</aside>\n            <div class="content-wrapper">\n                ${main}\n            </div>\n            </div>\n            `;
        }
        else {
            let cards = '';
            if (this._profiles[this._pageNumber]?.length) {
                cards = this._profiles[this._pageNumber].map(profile => {
                    let logo = profile?.orgLogo?.publicUrl ?? '/wp-content/themes/ace/assets/fallback_new.jpeg';
                    let alt = profile?.orgLogo?.altText ?? '';
                    return html `\n                    <div class="bg_white card card--location">\n                        <div class="container container--threetwo">\n                            <img src="${logo}" alt="${alt}" />\n                            <allin-alt-block description="${alt}" uid="alt_photo1_${profile.id}"></allin-alt-block>\n                        </div>\n                        <div class="card-content">\n                            <a\n                                href=${`/listings/support/${profile.id}`}\n                                @click=${(e) => this._onProfileClick(e, profile.id)}\n                                class="card_target"\n                            >\n                                <h3>${profile.name}</h3>\n                            </a>\n                            ${arrow}\n                        </div>\n                    </div>`;
                });
            }
            aside = html `<div class="sidebar">\n            <allin-listings-filters\n                name="${this.name}"\n                show="checkboxes"\n                supporttype='${JSON.stringify(this._supportTypes ?? [])}'\n                listingtype="support-profiles"\n                artform='${JSON.stringify(this._artforms ?? [])}'\n                operationIn='${JSON.stringify(this._operationIn ?? [])}'\n                operationIns='${JSON.stringify(this.operationIn ?? [])}'\n                supportTypes='${JSON.stringify(this.supportTypes ?? [])}'\n                artforms='${JSON.stringify(this.artforms ?? [])}'\n                languages='${JSON.stringify(this.languages ?? [])}'\n                language='${this._language ?? 'en'}'\n            ></allin-listings-filters></div>`;
            main = html `\n                <allin-listings-filters\n                    name="${this.name}"\n                    show="search"\n                    total="${this._total ?? 0}"\n                    search="${this._search ?? ''}"\n                    listingtype="support-profiles"\n                    addresssearch="${this._addressSearch ?? ''}"\n                    supporttypelist='${JSON.stringify(this._supportTypes ?? [])}'\n                    language='${this._language ?? 'en'}'\n                    artformslist='${JSON.stringify(this._artforms ?? [])}'\n                    operationinlist='${JSON.stringify(this._operationIn ?? [])}'\n                    supportTypes='${JSON.stringify(this.supportTypes ?? [])}'\n                    artforms='${JSON.stringify(this.artforms ?? [])}'\n                    operationins='${JSON.stringify(this.operationIn ?? [])}'\n                >\n                </allin-listings-filters>\n                <aside>${aside}</aside>\n                <div class="grid grid_33 gap cards">\n                    ${cards}\n                </div>\n                <allin-pagination\n                    total="${this._total ?? 0}"\n                    currentpage="${this._pageNumber ?? 1}"\n                    pagesize="${this.pageSize ?? 9}"\n                    name="${this.name}"\n                >\n                </allin-pagination>\n            `;
            return html `<div class="grid two_column">\n                <slot name="above-main"></slot>\n                ${main}\n            </div>\n            `;
        }
    }
    _updateSidebar(e) {
        this._currentProfileSidebar = e.detail.sidebar;
    }
    _handleGoBack() {
        this._currentProfile = '';
        this._currentProfileSidebar = '';
        if (this.params.currentProfile) {
            this.params.currentProfile = '';
        }
        this._fetchProfiles();
    }
    _selectProfile(profile) {
        this._currentProfile = profile;
    }
    //New _selectLocation to allow opening on new tab
    _newSelectProfile(profileId) {
        // clear location state data
        this._currentProfileData = null;
        this.params = {
            ...this.params,
            currentProfile: profileId
        };
        this._currentProfile = profileId;
    }
    _handleChangePage(e) {
        const detail = e.detail;
        if ('page' in detail && detail.page !== this._pageNumber) {
            this._pageNumber = detail.page;
            if (!this._profiles[this._pageNumber] || !this._profiles[this._pageNumber].length) {
                this._fetchProfiles();
            }
        }
    }
    _updateCurrentProfile(e) {
        this._currentProfileData = e.detail;
    }
    async _setFilters(e) {
        const detail = e.detail;
        let shouldFetch = false;
        if ('search' in detail && detail.search !== this._search) {
            this._search = detail.search;
            shouldFetch = true;
        }
        else if (this._search && detail.show == 'search') {
            this._search = '';
            shouldFetch = true;
        }
        if ('addressSearch' in detail && detail.addressSearch !== this._addressSearch) {
            this._addressSearch = detail.addressSearch;
            shouldFetch = true;
        }
        else if (this._addressSearch && detail.show == 'search') {
            this._addressSearch = '';
            shouldFetch = true;
        }
        /*  if('personalisation' in detail && JSON.stringify(detail.personalisation) !== JSON.stringify(this._personalisation)){
             this._personalisation = detail.personalisation;
             shouldFetch = true;
         } */
        if ('artform' in detail && JSON.stringify(detail.artform) !== JSON.stringify(this._artforms)) {
            this._artforms = detail.artform;
            if (!Array.isArray(this._artforms)) {
                this._artforms = [this._artforms];
            }
            shouldFetch = true;
        }
        else if (this._artforms && this._artforms.length && detail.show == 'checkboxes') {
            //this._artforms=[];
            this._artforms = detail.artforms; // prevents artforms data from being erased when adding other filter categories
            shouldFetch = true;
        }
        if ('supportType' in detail && JSON.stringify(detail.supportType) !== JSON.stringify(this._supportTypes)) {
            this._supportTypes = detail.supportType;
            if (!Array.isArray(this._supportTypes)) {
                this._supportTypes = [this._supportTypes];
            }
            shouldFetch = true;
        }
        else if (this._supportTypes && this._supportTypes.length && detail.show == 'checkboxes') {
            //this._supportTypes=[];
            this._supportTypes = detail.supportType; // prevents supportType data from being erased when adding other filter categories
            shouldFetch = true;
        }
        if ('operationIn' in detail && JSON.stringify(detail.operationIn) !== JSON.stringify(this._operationIn)) {
            this._operationIn = detail.operationIn;
            if (!Array.isArray(this._operationIn)) {
                this._operationIn = [this._operationIn];
            }
            shouldFetch = true;
        }
        else if (this._operationIn && this._operationIn.length && detail.show == 'checkboxes') {
            //this._operationIn=[];
            this._operationIn = detail.operationIn; // prevents operationIn data from being erased when adding other filter categories
            shouldFetch = true;
        }
        if ('pageNumber' in detail && detail.pageNumber !== this._pageNumber) {
            this._pageNumber = detail.pageNumber;
            shouldFetch = true;
        }
        if ('language' in detail && detail.language !== this._language) {
            this._language = detail.language;
            shouldFetch = true;
        }
        if (JSON.stringify(detail) === "{}" || 'reset' in detail) {
            shouldFetch = true;
            this._search = '';
            this._addressSearch = '';
            this._supportTypes = [];
            this._operationIn = [];
            this._artforms = [];
            this._pageNumber = 0;
            this._language = this.baseLanguage ?? 'en';
        }
        this.params = {
            ...this.params,
            supportTypes: this._supportTypes,
            operationIn: this._operationIn,
            artforms: this._artforms,
            addressSearch: this._addressSearch,
            search: this._search,
            language: this._language,
        };
        if (shouldFetch) {
            this._pageNumber = 1;
            await this._fetchProfiles();
        }
    }
    async _fetchProfiles(page = null) {
        // Fetch locations based on current filters
        const params = new URLSearchParams();
        if (this._search)
            params.append('search', this._search);
        if (this._addressSearch)
            params.append('addressSearch', this._addressSearch);
        if (this._personalisation)
            params.append('personalisation', JSON.stringify(this._personalisation));
        if (this._artforms && this._artforms.length)
            params.append('artforms', this._artforms.join(','));
        if (this._supportTypes && this._supportTypes.length)
            params.append('supportTypes', this._supportTypes.join(','));
        if (this._operationIn && this._operationIn.length)
            params.append('operationIn', this._operationIn.join(','));
        if (this._pageNumber)
            params.append('pageNumber', this._pageNumber);
        if (this._language)
            params.append('language', this._language);
        if (page)
            params.append('pageNumber', page);
        if (!page) {
            page = this._pageNumber;
        }
        this.eventManager.addHistory(`/listings/support?${params.toString()}`);
        if (this.perpage)
            params.append('perpage', this.perpage);
        try {
            this.grid?.classList.add('loading');
            const response = await fetch(`${this.restUrl}?action=get_support_profiles&${params.toString()}`, {
                headers: {
                    'AllIn-Nonce': this.nonce,
                    'AllIn-Public': 'public',
                }
            });
            //show logged out modal if session has expired
            if (response.status === 403) {
                handle403s();
                return;
            }
            const data = await response.json();
            if (data) {
                this._profiles = { ...this._profiles, [page]: data.data ?? [] };
                this._total = data.total ?? 0;
                // After fetching profile data, then scroll to the top of the updated 'allin-listings-support-profile-manager'
                const container = document.querySelector('allin-listings-support-profile-manager');
                if (container) {
                    container.scrollIntoView({
                        behavior: 'smooth',
                        block: 'start'
                    });
                }
            }
        }
        catch (error) {
            console.error('Error fetching support profiles:', error);
        }
        finally {
            this.grid?.classList.remove('loading');
        }
    }
    _onProfileClick(e, profileId) {
        // Only intercept plain left-clicks
        const isPlainLeftClick = e.button === 0 &&
            !e.metaKey &&
            !e.ctrlKey &&
            !e.shiftKey &&
            !e.altKey;
        if (!isPlainLeftClick) {
            return; // Let browser handle ctrl+click, middle-click, etc.
        }
        e.preventDefault();
        this._newSelectProfile(profileId);
    }
    static styles = [
        accessibility,
        grid,
        containers,
        sidebar,
        css `

        .bg{
            padding:var(--allin-padding,2rem);
            background-color:var(--allin-bg-color,#ccc);
            display:grid;
            gap:1rem;
        }
        .small{
            font-size:0.875rem;
        }


        h3,p{
            margin:0;
        }

        .button-group{
            display:flex;
            justify-content:space-between;
            margin-top:2rem;
        }

        .border-top{
            border-top:1px solid var(--allin-highlight-color,#472961);
            padding-top:2rem;
        }
        .button-group button, .return{
            background-color:transparent;
            color:var(--allin-highlight-color,#472961);
            border:0;
            display:flex;
            gap:0.5rem;
            align-items:center;
            text-decoration:underline;
            cursor:pointer;
            transition:color var(--transition) ease;
            font-weight:700;
            font-size:1rem;

        }
        .return{
            padding:0;
            margin-bottom:2rem;
        }
        .return a {
            font-size: 1.25rem;
            font-weight: 700;
        }


        .button-group button svg, .return svg{
            transition:transform var(--transition) ease;
        }
        .button-group button:hover, .return:hover{
            text-decoration-thickness:2px;
        }

        .button-group button:hover svg, .return:hover svg{
            transform:rotate(15deg);
        }

        ul{
            list-style:none;
            padding:0;
            margin:0;
        }

        button.back{
            color:var(--allin-highlight-color,#472961);
            background-color:transparent;
            border:0;
            padding:0;
            font-weight:700;
            display:flex;
            align-items:center;
            gap:0.5rem;
        }
        button.back span{
            display:flex;
            align-items:center;
            justify-content:center;
            width:1rem;
        }
        button.back svg{
            transform:rotate(0);
            transition:transform var(--transition) ease;
            width:1rem;
        }
        button.back:hover{
            text-decoration:underline;
        }

        button.back:hover svg{
            transform:rotate(15deg);
        }
        @media (prefers-reduced-motion: reduce) {
            button.back svg{
                transform:none !important;
            }
        }
        @media (min-width: 1000px) {
            .two_column{
                grid-template-rows:auto auto auto auto auto;
                grid-auto-flow:dense;
                column-gap:4rem;
            }
            aside{
                grid-row:1 / -1;
                grid-column:2 / 3;
            }
        }

        .card{
            background-color:#fff;
            display:grid;
            grid-template-rows:auto 1fr;
            img{
                transition:opacity var(--transition) ease;
            }
        }

        .card .card-content>svg{
            transition:transform var(--transition) ease;
            display:block;
            margin-left:auto;
        }

        .card-content{
            padding:1rem;
        }

        .card a{
            color:var(--allin-highlight-color,#472961);
            text-decoration-gap:0.25rem;
            text-decoration-thickness:1px;
            transition:text-decoration-thickness var(--transition) ease;
        }
        .card:hover{
            a{
                text-decoration-thickness:2px;
            }
            img{
                opacity:0.9;
            }

        }

        .card:hover .card-content>svg{
            transform:translateX(0.5rem);
        }

        .card-content{
            display:grid;
            grid-template-rows:auto auto 1fr auto;
        }

        .accessibility-icons{
            display:flex;
            gap:0.75rem;
            margin:3rem 0;
            flex-wrap:wrap;
        }
        .accessibility-icons li{
            width:1.5rem;
            height:1.5rem;
            path,circle,rect,polygon{
                fill:var(--allin-highlight-color,#472961);
            }

        }

        .loading {
            position: relative;
        }

        .loading::after {
            content: '';
            position: absolute;
            top: 50%;
            left: 50%;
            width: 2rem;
            height: 2rem;
            animation: spin 1s linear infinite;
            z-index: 100;
            background-image: url(/wp-content/themes/ace/assets/wheel.svg);
            background-size: cover;
        }

        @keyframes spin {
            from { transform: translate(-50%, -50%) rotate(0deg); }
            to   { transform: translate(-50%, -50%) rotate(360deg); }
        }

        .location_events{
            margin-top:4rem;
            position:relative;
        }
        .location_all_events{
            position:relative;
        }

        .location_events::before,.location_all_events::before{
            content:'';
            position:absolute;
            top:0;
            bottom:0;
            background-color:var(--allin-bg-color,#ccc);
            z-index:0;
            width:100vw;
            left:50%;
            transform:translateX(-50%);
        }

        allin-location-events, allin-location-zones{
            position:relative;
            z-index:1;
        }
        aside.sidebar .grid--icon .icon_text p a {
            white-space: normal;
            overflow-wrap: break-word;
            word-break: break-word;
            display: inline;
        }
        @media screen and (max-width: 999px) {
            aside.sidebar{
                display: none;
            }
        }

        .grid.two_column .return.back-to-search a{
            text-decoration: none;
            color: #472961;
        }

        .sidebar .return{
            margin-top:2rem;
        }

        `
    ];
}
